/**
 * Right-rail "Recently viewed tickets" query.
 *
 * The agent detail page records a view as a side effect of GET on the
 * ticket detail (see backend ticket_view_service); this hook just reads
 * back the 10 most recent. We re-fetch on every navigation between
 * tickets - the rail-on-page-N becomes the rail-on-page-N+1 only after
 * the detail GET of page N+1 has landed, so a short polling interval
 * keeps the order honest if the agent clicks fast.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type { RecentlyViewedTicketResponse } from "@/types/api";

export const recentlyViewedTicketsQueryKey = (limit: number) =>
  ["agent", "tickets", "recently-viewed", limit] as const;

export interface UseRecentlyViewedTicketsOptions {
  enabled?: boolean;
  limit?: number;
  refetchIntervalMs?: number | false;
}

export function useRecentlyViewedTickets(
  options: UseRecentlyViewedTicketsOptions = {},
): UseQueryResult<RecentlyViewedTicketResponse[]> {
  const limit = options.limit ?? 10;
  return useQuery({
    queryKey: recentlyViewedTicketsQueryKey(limit),
    enabled: options.enabled !== false,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    queryFn: async () => {
      const response = await api.get<RecentlyViewedTicketResponse[]>(
        `/agent/tickets/recently-viewed`,
        { params: { limit } },
      );
      return response.data;
    },
  });
}
